// =============================================================================
// licenceExportPack.ts — Task #14
//
// Builds a ZIP of a person's licence photos with proper filenames and
// triggers a download. Used for client onboarding compliance packs:
// manager ticks which licences are needed → click Download pack → upload
// the ZIP into the client's portal.
//
// Filename convention matches the legacy ticket library:
//   Licences - {Employee Name} - YYYY-MM-DD.zip
//     └── {Employee Name}/
//          ├── {Licence Type} - {Employee Name} - Exp YYYYMMDD_FRONT.jpg
//          ├── {Licence Type} - {Employee Name} - Exp YYYYMMDD_BACK.jpg
//          └── ...
//
// Pure frontend — uses JSZip (already in deps via project folder templates).
// =============================================================================

import JSZip from "jszip";
import { supabase } from "@/lib/supabase";
import { buildLicenceFilename } from "./licenceHelpers";
import { safeFilenamePart } from "@/lib/storageDownload";

export interface ExportLicence {
  licence_type_display: string | null;
  expiry_date:          string | null;
  front_photo_url:      string | null;
  back_photo_url:       string | null;
}

export interface DownloadLicencePackOpts {
  employeeFirstName: string | null;
  employeeLastName:  string | null;
  licences:          ExportLicence[];
  /**
   * Per-photo progress callback — fires after each photo finishes downloading
   * so the UI can render a progress bar. Receives (done, total).
   */
  onProgress?: (done: number, total: number) => void;
}

export async function downloadLicencePack(opts: DownloadLicencePackOpts): Promise<void> {
  if (opts.licences.length === 0) return;

  const employeeName = [opts.employeeFirstName, opts.employeeLastName]
    .filter(Boolean)
    .join(" ")
    .trim() || "Employee";
  const folderName = safeFilenamePart(employeeName) || "Employee";

  const zip = new JSZip();
  const folder = zip.folder(folderName)!;

  // Count total photos for progress callback
  const totalPhotos = opts.licences.reduce(
    (n, l) => n + (l.front_photo_url ? 1 : 0) + (l.back_photo_url ? 1 : 0),
    0,
  );
  let done = 0;
  const tick = () => {
    done += 1;
    opts.onProgress?.(done, totalPhotos);
  };

  // Fetch + add each photo in parallel — much faster than serial for 20+ photos
  const fetches: Promise<void>[] = [];

  function addPhoto(path: string, side: "front" | "back", licenceTypeDisplay: string | null, expiry: string | null) {
    fetches.push((async () => {
      const filename = buildLicenceFilename({
        licence_type_display: licenceTypeDisplay,
        holder_first_name:    opts.employeeFirstName,
        holder_last_name:     opts.employeeLastName,
        expiry_date:          expiry,
        side,
        storage_path:         path,
      });

      // Generate a short-TTL signed URL just for the in-memory fetch.
      // No Content-Disposition needed — we're streaming bytes into the ZIP.
      const { data, error } = await supabase.storage
        .from("licence-documents")
        .createSignedUrl(path, 60);
      if (error || !data?.signedUrl) {
        throw new Error(`Couldn't sign URL for ${filename}: ${error?.message ?? "unknown"}`);
      }

      const res = await fetch(data.signedUrl);
      if (!res.ok) {
        throw new Error(`Couldn't fetch ${filename}: HTTP ${res.status}`);
      }
      const blob = await res.blob();
      folder.file(filename, blob);
      tick();
    })());
  }

  for (const l of opts.licences) {
    if (l.front_photo_url) addPhoto(l.front_photo_url, "front", l.licence_type_display, l.expiry_date);
    if (l.back_photo_url)  addPhoto(l.back_photo_url,  "back",  l.licence_type_display, l.expiry_date);
  }

  await Promise.all(fetches);

  // If a licence has no photos, drop a small text file as a placeholder so
  // the manager knows that licence was ticked but had no scan on file yet.
  const noPhoto = opts.licences.filter(l => !l.front_photo_url && !l.back_photo_url);
  if (noPhoto.length > 0) {
    const lines = noPhoto.map(l => `- ${l.licence_type_display ?? "Unknown"} (expiry ${l.expiry_date ?? "—"}) — no photo on file`);
    folder.file("Missing photos.txt",
      `The following licences were included in your selection but don't have a photo on record yet:\n\n${lines.join("\n")}\n`);
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  const dateStamp = new Date().toISOString().slice(0, 10);
  const zipName = safeFilenamePart(`Licences - ${employeeName} - ${dateStamp}`) + ".zip";

  triggerDownload(zipBlob, zipName);
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a tick to start the download before we release the URL
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
